import React, { useState, useEffect, useRef } from 'react';
import { Scale, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

const WeightInputModal = ({ isOpen, onSubmit, onCancel, boxNumber, itemTitle }) => {
  const [weight, setWeight] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Auto-focus the input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const validateWeight = (value) => {
    const numValue = parseFloat(value);

    if (isNaN(numValue) || value.trim() === '') {
      return 'Please enter a valid weight';
    }

    if (numValue < 0.01) {
      return 'Weight must be at least 0.01 kg';
    }

    if (numValue > 50) {
      return 'Weight exceeds maximum limit of 50 kg';
    }

    return '';
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent spam clicking

    const validationError = validateWeight(weight);

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(parseFloat(weight));
      setWeight('');
      setError('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return; // Prevent canceling while submitting
    setWeight('');
    setError('');
    setIsSubmitting(false);
    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Scale className="w-6 h-6 text-blue-600" />
            Weight Required
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-900">
              <span className="font-bold">Weight Missing!</span> This item needs a weight to calculate box totals.
            </p>
            {itemTitle && (
              <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                {itemTitle}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Item Weight (kg)
            </label>
            <div className="relative">
              <Input
                ref={inputRef}
                type="number"
                value={weight}
                onChange={(e) => {
                  setWeight(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                onFocus={(e) => e.target.select()}
                className={`h-12 text-center text-lg ${error ? 'border-red-500' : ''}`}
                placeholder="Enter weight (e.g., 0.5)"
                step="0.01"
                min="0.01"
                max="50"
                disabled={isSubmitting}
              />
              <div className="absolute right-3 top-3 text-gray-400 text-sm">kg</div>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs text-gray-600">
            <p><strong>Valid range:</strong> 0.01 kg - 50 kg</p>
            <p><strong>Note:</strong> This weight will be saved for this item and used for all future calculations</p>
            <p className="text-green-700 font-semibold">✓ No need to rescan - the item will be automatically added to the box!</p>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="h-10 px-4"
            disabled={isSubmitting}
          >
            Cancel Scan
          </Button>
          <Button
            onClick={handleSubmit}
            className="h-10 px-6 bg-blue-600 hover:bg-blue-700"
            disabled={!weight || !!validateWeight(weight) || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              'Set Weight & Add to Box'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WeightInputModal;
